import type { StudyGroupViewKey } from "./types";

const isPositiveIntegerSegment = (value: string | undefined): boolean =>
  value !== undefined && /^[1-9]\d*$/.test(value);

const asPositiveInteger = (value: string): number => Number(value);

const parseInternalPathname = (value: string): string | null => {
  if (!value.startsWith("/") || value.startsWith("//")) return null;

  try {
    const queryOrHashIndex = value.search(/[?#]/);
    const rawPathname =
      queryOrHashIndex === -1 ? value : value.slice(0, queryOrHashIndex);
    const pathname = new URL(value, "https://study-group-view-state.invalid")
      .pathname;
    return pathname === rawPathname ? pathname : null;
  } catch {
    return null;
  }
};

export const deriveStudyGroupViewKey = (
  value: string,
  groupId: number,
): StudyGroupViewKey | null => {
  if (!Number.isSafeInteger(groupId) || groupId <= 0) return null;

  const pathname = parseInternalPathname(value);
  if (!pathname) return null;

  const pathnameSegments = pathname.split("/");
  if (pathnameSegments[0] !== "") return null;
  if (pathnameSegments.at(-1) === "") pathnameSegments.pop();
  if (pathnameSegments.slice(1).some((segment) => segment === "")) {
    return null;
  }
  const segments = pathnameSegments.slice(1);
  if (
    segments[0] !== "study-group" ||
    !isPositiveIntegerSegment(segments[1]) ||
    asPositiveInteger(segments[1]) !== groupId
  ) {
    return null;
  }

  const route = segments.slice(2);
  if (route.length === 1 && route[0] === "stream") return "stream:home";
  if (
    route.length === 2 &&
    route[0] === "channel" &&
    isPositiveIntegerSegment(route[1])
  ) {
    return `stream:channel:${asPositiveInteger(route[1] ?? "")}`;
  }
  if (
    route.length === 3 &&
    route[0] === "stream" &&
    route[1] === "channel" &&
    isPositiveIntegerSegment(route[2])
  ) {
    return `stream:channel:${asPositiveInteger(route[2] ?? "")}`;
  }
  if (route.length === 1 && route[0] === "events") return "events";
  if (route.length === 1 && route[0] === "files") return "files:folder:root";
  if (
    route.length === 3 &&
    route[0] === "files" &&
    route[1] === "folder" &&
    isPositiveIntegerSegment(route[2])
  ) {
    return `files:folder:${asPositiveInteger(route[2] ?? "")}`;
  }
  if (route.length === 1 && route[0] === "link-list") {
    return "links:category:root";
  }
  if (
    route.length === 3 &&
    route[0] === "link-list" &&
    route[1] === "category" &&
    isPositiveIntegerSegment(route[2])
  ) {
    return `links:category:${asPositiveInteger(route[2] ?? "")}`;
  }

  return null;
};
